import React, { useState, useEffect } from 'react';
import { Form, Table, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { adminUserApi, adminWarehouseApi } from 'api';
import { PERMISSION_OPTIONS } from 'constants/admin/user';

const buildPermissionMap = (warehousePermissions = []) => {
  const map = {};
  warehousePermissions.forEach(perm => {
    map[perm.warehouse.id] = {
      enabled: true,
      is_manager: perm.is_manager,
      gate_pass: perm.gate_pass,
      put_away: perm.put_away,
    };
  });
  return map;
};

const WarehousePermissionEdit = ({ user, onSuccess, onCancel }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [permMap, setPermMap] = useState(() =>
    buildPermissionMap(user?.warehouse_permissions),
  );
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await adminWarehouseApi.getList({ page_size: 9999 });
        setWarehouses(res?.results || res || []);
      } catch {
        toast.error('Failed to load warehouse list');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchWarehouses();
  }, []);

  const handleToggle = (warehouseId, checked) => {
    setPermMap(prev => ({
      ...prev,
      [warehouseId]: {
        is_manager: false,
        gate_pass: 0,
        put_away: 0,
        ...prev[warehouseId],
        enabled: checked,
      },
    }));
  };

  const handleChange = (warehouseId, field, value) => {
    setPermMap(prev => ({
      ...prev,
      [warehouseId]: { ...prev[warehouseId], [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = Object.entries(permMap)
        .filter(([, perm]) => perm.enabled)
        .map(([warehouseId, perm]) => ({
          warehouse_id: Number(warehouseId),
          is_manager: perm.is_manager,
          gate_pass: perm.gate_pass,
          put_away: perm.put_away,
        }));

      await adminUserApi.updatePermissions(user.id, payload);
      toast.success('Permissions updated successfully');
      onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.permissions?.[0] || 'Failed to update permissions';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <p className="text-muted mb-0">Loading warehouses...</p>;
  }

  if (!warehouses.length) {
    return (
      <>
        <p className="text-muted">No warehouses available.</p>
        <div className="d-flex justify-content-end">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Table responsive bordered size="sm" className="mb-3">
        <thead>
          <tr>
            <th className="text-center">Assign</th>
            <th>Warehouse</th>
            <th className="text-center">Manager</th>
            <th className="text-center">Gate Pass</th>
            <th className="text-center">Put Away</th>
          </tr>
        </thead>
        <tbody>
          {warehouses.map(wh => {
            const perm = permMap[wh.id];
            const enabled = !!perm?.enabled;

            return (
              <tr key={wh.id} className={!enabled ? 'table-light' : undefined}>
                <td className="text-center align-middle">
                  <Form.Check
                    type="checkbox"
                    checked={enabled}
                    onChange={e => handleToggle(wh.id, e.target.checked)}
                  />
                </td>
                <td className="align-middle">{wh.name}</td>
                <td className="text-center align-middle">
                  <Form.Check
                    type="switch"
                    checked={enabled && !!perm?.is_manager}
                    disabled={!enabled}
                    onChange={e =>
                      handleChange(wh.id, 'is_manager', e.target.checked)
                    }
                  />
                </td>
                <td className="text-center">
                  <Form.Select
                    size="sm"
                    value={enabled ? perm?.gate_pass ?? 0 : 0}
                    disabled={!enabled}
                    onChange={e =>
                      handleChange(wh.id, 'gate_pass', Number(e.target.value))
                    }
                  >
                    {PERMISSION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td className="text-center">
                  <Form.Select
                    size="sm"
                    value={enabled ? perm?.put_away ?? 0 : 0}
                    disabled={!enabled}
                    onChange={e =>
                      handleChange(wh.id, 'put_away', Number(e.target.value))
                    }
                  >
                    {PERMISSION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Permissions'}
        </Button>
      </div>
    </>
  );
};

export default WarehousePermissionEdit;
